import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import {
  WebappRelease,
  WebappUpdateScope,
  WebappUpdateStatusData,
  WebappUpdateStoreRow,
  WebappUpdateTerminalRow
} from './webapp-update-types';

type AssignDialogScope = 'selected-terminals' | 'filtered-stores' | 'all-stores';

@Component({
  selector: 'app-webapp-update-control',
  templateUrl: './webapp-update-control.component.html',
  styleUrls: ['./webapp-update-control.component.scss']
})
export class WebappUpdateControlComponent implements OnInit, OnChanges, OnDestroy {

  @Input() storeId: string = '';

  readonly INHERIT_VALUE = '__inherit__';

  role = localStorage.getItem('role') || '';
  isAdmin = false;
  isReadOnly = false;

  releases: WebappRelease[] = [];
  latestReleaseId: string | null = null;
  showRetired = false;

  statusData: WebappUpdateStatusData | null = null;
  stores: WebappUpdateStoreRow[] = [];
  allTerminals: WebappUpdateTerminalRow[] = [];
  filteredTerminals: WebappUpdateTerminalRow[] = [];

  filterStoreId = '';
  terminalSearch = '';

  selectedTerminalIds = new Set<string>();

  autoRefreshEnabled = true;
  refreshIntervalSec = 15;
  countdown = 0;

  isLoading = false;
  errorMessage = '';
  endpointUnavailable = false;

  showRegisterDialog = false;
  registerForm = { commit: '', token: '', releaseNotes: '' };

  showAssignDialog = false;
  assignDialogReleaseId: string | null | typeof this.INHERIT_VALUE = this.INHERIT_VALUE;
  assignDialogScope: AssignDialogScope = 'selected-terminals';

  showPushDialog = false;
  pushForce = false;
  pushMessage = '';
  pushDialogScope: AssignDialogScope = 'selected-terminals';

  editingRelease: WebappRelease | null = null;
  editNotesText = '';

  private refreshSub?: Subscription;
  private countdownSub?: Subscription;
  private loadSeq = 0;

  constructor(
    private api: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.role === 'admin';
    this.isReadOnly = this.role === 'chain_manager';
    this.syncFilterFromInput();
    this.refreshAll(true);
    this.countdown = this.refreshIntervalSec;
    this.startAutoRefresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange) {
      this.syncFilterFromInput();
      this.selectedTerminalIds.clear();
      this.refreshAll(true);
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  private syncFilterFromInput(): void {
    this.filterStoreId = this.storeId || '';
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.countdown = this.refreshIntervalSec;
    this.refreshSub = interval(this.refreshIntervalSec * 1000).subscribe(() => {
      this.refreshAll(false);
      this.countdown = this.refreshIntervalSec;
    });
    this.countdownSub = interval(1000).subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      }
    });
  }

  stopAutoRefresh(): void {
    this.refreshSub?.unsubscribe();
    this.countdownSub?.unsubscribe();
  }

  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  refreshAll(showSpinner: boolean): void {
    const seq = ++this.loadSeq;
    if (showSpinner) {
      this.isLoading = true;
      this.spinner.show();
    }
    this.errorMessage = '';
    this.endpointUnavailable = false;

    const releasesUrl = `${ApiUrl.getWebappReleases}?includeRetired=${this.showRetired}`;
    const statusQuery = this.filterStoreId ? `?storeId=${encodeURIComponent(this.filterStoreId)}` : '';
    const statusUrl = `${ApiUrl.getWebappUpdateStatus}${statusQuery}`;

    let releasesDone = false;
    let statusDone = false;

    const finishIfReady = () => {
      if (!releasesDone || !statusDone || seq !== this.loadSeq) {
        return;
      }
      this.applyTerminalFilters();
      this.isLoading = false;
      this.spinner.hide();
    };

    this.api.getRequest(releasesUrl).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        const data = res?.response?.data ?? {};
        this.releases = data.releases || [];
        this.latestReleaseId = data.latestReleaseId ?? null;
        releasesDone = true;
        finishIfReady();
      },
      error: (err: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.handleLoadError(err);
        releasesDone = true;
        statusDone = true;
        finishIfReady();
      }
    });

    this.api.getRequest(statusUrl).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        const data = res?.response?.data ?? {};
        this.statusData = data;
        this.stores = data.stores || [];
        this.allTerminals = data.terminals || [];
        statusDone = true;
        finishIfReady();
      },
      error: (err: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        if (!this.errorMessage) {
          this.handleLoadError(err);
        }
        statusDone = true;
        finishIfReady();
      }
    });
  }

  private handleLoadError(err: any): void {
    const msg = err?.error?.response?.message || 'Failed to load webapp update data';
    if (err?.status === 404) {
      this.endpointUnavailable = true;
      this.errorMessage = msg;
      return;
    }
    this.errorMessage = msg;
    this.toastr.error(msg);
  }

  onShowRetiredChange(): void {
    this.refreshAll(true);
  }

  onFilterStoreChange(): void {
    this.selectedTerminalIds.clear();
    this.refreshAll(true);
  }

  applyTerminalFilters(): void {
    let rows = [...this.allTerminals];
    if (this.filterStoreId) {
      rows = rows.filter(t => t.storeId === this.filterStoreId);
    }
    const q = this.terminalSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter(t =>
        (t.terminalName || '').toLowerCase().includes(q) ||
        t.terminalId.toLowerCase().includes(q)
      );
    }
    this.filteredTerminals = rows;
    const visible = new Set(rows.map(r => r.terminalId));
    this.selectedTerminalIds.forEach(id => {
      if (!visible.has(id)) {
        this.selectedTerminalIds.delete(id);
      }
    });
  }

  onTerminalSearchChange(): void {
    this.applyTerminalFilters();
  }

  activeReleases(): WebappRelease[] {
    return this.releases.filter(r => !r.retired);
  }

  releaseLabel(releaseId: string | null | undefined): string {
    if (!releaseId) {
      return 'Inherit latest';
    }
    const r = this.releases.find(x => x._id === releaseId);
    if (!r) {
      return releaseId.slice(-6);
    }
    return `${r.commit}${r.isLatest ? ' (latest)' : ''}`;
  }

  formatTime(dateStr?: string | null): string {
    if (!dateStr) {
      return '—';
    }
    return new Date(dateStr).toLocaleString();
  }

  truncateToken(token?: string): string {
    if (!token) {
      return '—';
    }
    return token.length > 22 ? token.slice(0, 22) + '…' : token;
  }

  targetSourceLabel(source?: string): string {
    switch (source) {
      case 'terminal': return 'Terminal pin';
      case 'store': return 'Store default';
      case 'latest': return 'Latest';
      case 'none': return 'None';
      default: return '—';
    }
  }

  terminalStatus(row: WebappUpdateTerminalRow): 'force' | 'update' | 'ok' | 'unknown' {
    if (row.forcePending) {
      return 'force';
    }
    if (row.updateRequired) {
      return 'update';
    }
    if (row.reportedCommit && row.target?.commit && row.reportedCommit === row.target.commit) {
      return 'ok';
    }
    if (!row.updateRequired && row.target?.source !== 'none' && row.reportedCommit) {
      return 'ok';
    }
    return 'unknown';
  }

  toggleTerminalSelection(terminalId: string, checked: boolean): void {
    if (checked) {
      this.selectedTerminalIds.add(terminalId);
    } else {
      this.selectedTerminalIds.delete(terminalId);
    }
  }

  allFilteredSelected(): boolean {
    return this.filteredTerminals.length > 0 &&
      this.filteredTerminals.every(t => this.selectedTerminalIds.has(t.terminalId));
  }

  toggleSelectAllFiltered(checked: boolean): void {
    if (checked) {
      this.filteredTerminals.forEach(t => this.selectedTerminalIds.add(t.terminalId));
    } else {
      this.filteredTerminals.forEach(t => this.selectedTerminalIds.delete(t.terminalId));
    }
  }

  openRegisterDialog(): void {
    this.registerForm = { commit: '', token: '', releaseNotes: '' };
    this.showRegisterDialog = true;
  }

  closeRegisterDialog(): void {
    this.showRegisterDialog = false;
  }

  submitRegisterRelease(): void {
    const { commit, token, releaseNotes } = this.registerForm;
    if (!commit.trim() || !token.trim()) {
      Swal.fire('Validation', 'Commit and token are required.', 'warning');
      return;
    }
    Swal.fire({
      title: 'Register release?',
      text: 'Manual registration is a fallback when deploy registration did not run.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Register'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.api.postRequest(ApiUrl.postWebappRelease, {
        commit: commit.trim(),
        token: token.trim(),
        releaseNotes: releaseNotes.trim()
      }).subscribe({
        next: () => {
          this.spinner.hide();
          this.toastr.success('Release registered');
          this.showRegisterDialog = false;
          this.refreshAll(true);
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err?.error?.response?.message || 'Registration failed', 'error');
        }
      });
    });
  }

  openEditNotes(release: WebappRelease): void {
    this.editingRelease = release;
    this.editNotesText = release.releaseNotes || '';
  }

  closeEditNotes(): void {
    this.editingRelease = null;
  }

  saveReleaseNotes(): void {
    if (!this.editingRelease) {
      return;
    }
    const id = this.editingRelease._id;
    this.spinner.show();
    this.api.patchRequest(ApiUrl.patchWebappRelease(id), { releaseNotes: this.editNotesText }).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success('Notes updated');
        this.editingRelease = null;
        this.refreshAll(true);
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err?.error?.response?.message || 'Update failed', 'error');
      }
    });
  }

  confirmMarkLatest(release: WebappRelease): void {
    Swal.fire({
      title: 'Mark as latest?',
      html: 'This sets the fleet default for kiosks that inherit latest (no store or terminal pin). Other releases will no longer be marked latest.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Mark latest'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.patchRelease(release._id, { isLatest: true });
    });
  }

  confirmRetire(release: WebappRelease, retired: boolean): void {
    const action = retired ? 'Retire' : 'Un-retire';
    Swal.fire({
      title: `${action} release?`,
      text: retired
        ? 'Retired releases cannot be assigned. Ensure no stores or terminals still pin this release.'
        : 'This release will become assignable again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: action
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.patchRelease(release._id, { retired });
    });
  }

  private patchRelease(id: string, body: any): void {
    this.spinner.show();
    this.api.patchRequest(ApiUrl.patchWebappRelease(id), body).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success('Release updated');
        this.refreshAll(true);
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err?.error?.response?.message || 'Update failed', 'error');
      }
    });
  }

  onStoreDefaultChange(store: WebappUpdateStoreRow, value: string): void {
    const releaseId = value === this.INHERIT_VALUE ? null : value;
    Swal.fire({
      title: 'Change store default?',
      text: `Set default webapp release for ${store.storeName}. Terminals without a terminal pin will inherit this default.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Apply'
    }).then(result => {
      if (!result.isConfirmed) {
        this.refreshAll(false);
        return;
      }
      this.runAssign(releaseId, 'store', undefined, [store.storeId]);
    });
  }

  onTerminalPinChange(terminal: WebappUpdateTerminalRow, value: string): void {
    const releaseId = value === this.INHERIT_VALUE ? null : value;
    Swal.fire({
      title: 'Change terminal pin?',
      text: `Update pin for ${terminal.terminalName || terminal.terminalId}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Apply'
    }).then(result => {
      if (!result.isConfirmed) {
        this.refreshAll(false);
        return;
      }
      this.runAssign(releaseId, 'terminal', [terminal.terminalId], undefined);
    });
  }

  openAssignDialog(): void {
    this.assignDialogReleaseId = this.INHERIT_VALUE;
    this.assignDialogScope = this.selectedTerminalIds.size > 0
      ? 'selected-terminals'
      : (this.filterStoreId ? 'filtered-stores' : 'all-stores');
    this.showAssignDialog = true;
  }

  closeAssignDialog(): void {
    this.showAssignDialog = false;
  }

  submitAssignDialog(): void {
    const releaseId = this.assignDialogReleaseId === this.INHERIT_VALUE
      ? null
      : (this.assignDialogReleaseId as string);
    const scopePayload = this.resolveScopePayload(this.assignDialogScope);
    if (!scopePayload) {
      return;
    }
    Swal.fire({
      title: 'Assign release?',
      text: scopePayload.summary,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Assign'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      this.runAssign(releaseId, scopePayload.scope, scopePayload.terminalIds, scopePayload.storeIds);
      this.showAssignDialog = false;
    });
  }

  openPushDialog(force: boolean): void {
    this.pushForce = force;
    this.pushMessage = '';
    this.pushDialogScope = this.selectedTerminalIds.size > 0
      ? 'selected-terminals'
      : (this.filterStoreId ? 'filtered-stores' : 'all-stores');
    this.showPushDialog = true;
  }

  closePushDialog(): void {
    this.showPushDialog = false;
  }

  submitPushDialog(): void {
    const scopePayload = this.resolveScopePayload(this.pushDialogScope);
    if (!scopePayload) {
      return;
    }
    const force = this.pushForce;
    const doPush = () => {
      this.spinner.show();
      const body: any = {
        scope: scopePayload.scope,
        force
      };
      if (force && this.pushMessage.trim()) {
        body.message = this.pushMessage.trim();
      }
      if (scopePayload.terminalIds?.length) {
        body.terminalIds = scopePayload.terminalIds;
      }
      if (scopePayload.storeIds?.length) {
        body.storeIds = scopePayload.storeIds;
      }
      this.api.postRequest(ApiUrl.postWebappUpdatePush, body).subscribe({
        next: (res: any) => {
          this.spinner.hide();
          const data = res?.response?.data ?? {};
          Swal.fire({
            title: force ? 'Force update sent' : 'Update push sent',
            html: `<ul class="text-start mb-0">
              <li>Targeted terminals: <strong>${data.targetedTerminals ?? 0}</strong></li>
              <li>Delivered online: <strong>${data.onlineDelivered ?? 0}</strong></li>
              <li>Queued offline: <strong>${data.offlineQueued ?? 0}</strong></li>
              <li>Skipped (no target): <strong>${data.skippedNoTarget ?? 0}</strong></li>
            </ul>`,
            icon: 'success'
          });
          this.showPushDialog = false;
          this.refreshAll(true);
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err?.error?.response?.message || 'Push failed', 'error');
        }
      });
    };

    Swal.fire({
      title: force ? 'Force update?' : 'Push soft update?',
      text: scopePayload.summary,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: force ? 'Force update' : 'Push update'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      if (force && scopePayload.scope === 'all') {
        Swal.fire({
          title: 'Fleet-wide FORCE update',
          html: '<strong>Kiosks will reload even mid-shift.</strong> Payments in progress are deferred, but this affects every store. Continue?',
          icon: 'error',
          showCancelButton: true,
          confirmButtonText: 'Yes, force all kiosks',
          confirmButtonColor: '#dc3545'
        }).then(second => {
          if (second.isConfirmed) {
            doPush();
          }
        });
      } else {
        doPush();
      }
    });
  }

  private runAssign(
    releaseId: string | null,
    scope: WebappUpdateScope,
    terminalIds?: string[],
    storeIds?: string[]
  ): void {
    this.spinner.show();
    const body: any = { releaseId, scope };
    if (terminalIds?.length) {
      body.terminalIds = terminalIds;
    }
    if (storeIds?.length) {
      body.storeIds = storeIds;
    }
    this.api.postRequest(ApiUrl.postWebappUpdateAssign, body).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const data = res?.response?.data ?? {};
        this.toastr.success(`Assigned (${data.modifiedCount ?? 0} updated)`);
        this.refreshAll(true);
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err?.error?.response?.message || 'Assign failed', 'error');
        this.refreshAll(false);
      }
    });
  }

  private resolveScopePayload(dialogScope: AssignDialogScope): {
    scope: WebappUpdateScope;
    terminalIds?: string[];
    storeIds?: string[];
    summary: string;
  } | null {
    if (dialogScope === 'selected-terminals') {
      const ids = Array.from(this.selectedTerminalIds);
      if (!ids.length) {
        Swal.fire('Selection', 'Select at least one terminal.', 'warning');
        return null;
      }
      return {
        scope: 'terminal',
        terminalIds: ids,
        summary: `Apply to ${ids.length} selected terminal(s).`
      };
    }
    if (dialogScope === 'filtered-stores') {
      if (!this.filterStoreId) {
        Swal.fire('Store filter', 'Choose a store in the filter, or use all stores.', 'warning');
        return null;
      }
      const name = this.stores.find(s => s.storeId === this.filterStoreId)?.storeName || this.filterStoreId;
      return {
        scope: 'store',
        storeIds: [this.filterStoreId],
        summary: `Apply to store: ${name}.`
      };
    }
    return {
      scope: 'all',
      summary: 'Apply to all stores (terminal pins are not cleared).'
    };
  }
}
